import React, { useEffect, useMemo, useState } from 'react';

function PaginatedProjectPreview({
    sections,
    timeline,
    renderHeader,
    renderFooter,
}) {
    const [pages, setPages] = useState([[]]);

    const blocks = useMemo(() => {
        const cleanSections = (sections || [])
            .map((section, idx) => ({
                id: `sec-${idx}`,
                kind: 'section',
                section,
                page: Math.max(1, Number(section.page || 1)),
            }))
            .filter((item) => (item.section.content || '').trim());

        return cleanSections;
    }, [sections]);

    useEffect(() => {
        if (!blocks.length && !(timeline || '').trim()) {
            setPages([[]]);
            return;
        }

        const byPage = new Map();
        let maxPage = 1;

        blocks.forEach((block) => {
            const pageNumber = block.page || 1;
            maxPage = Math.max(maxPage, pageNumber);
            if (!byPage.has(pageNumber)) {
                byPage.set(pageNumber, []);
            }
            byPage.get(pageNumber).push(block);
        });

        if ((timeline || '').trim()) {
            const timelineBlock = {
                id: 'timeline',
                kind: 'timeline',
                content: timeline,
            };
            if (!byPage.has(maxPage)) {
                byPage.set(maxPage, [timelineBlock]);
            } else {
                byPage.get(maxPage).push(timelineBlock);
            }
        }

        const nextPages = Array.from({ length: maxPage }, (_, idx) => byPage.get(idx + 1) || []);

        setPages(nextPages);
    }, [blocks, timeline]);

    const renderBlock = (block) => {
        if (block.kind === 'timeline') {
            return (
                <div key={block.id} className="pg-project-block">
                    <div className="pg-section-title">PROJECT TIMELINE</div>
                    <p className="pg-body-text">{block.content}</p>
                </div>
            );
        }

        const section = block.section;

        if (section.type === 'page-heading') {
            return (
                <div key={block.id} className="pg-project-block">
                    <div className="pg-prev-page-heading">{section.content}</div>
                </div>
            );
        }

        if (section.type === 'heading') {
            return (
                <div key={block.id} className="pg-project-block">
                    <div className="pg-prev-heading">{section.content}</div>
                </div>
            );
        }

        if (section.type === 'sub-heading') {
            return (
                <div key={block.id} className="pg-project-block">
                    <div className="pg-prev-sub-heading">{section.content}</div>
                </div>
            );
        }

        if (section.type === 'bullet') {
            return (
                <div key={block.id} className="pg-project-block">
                    <div className="pg-prev-bullet-list">
                        {section.content
                            .split('\n')
                            .filter((line) => line.trim())
                            .map((line, idx) => (
                                <div key={`${block.id}-${idx}`} className="pg-prev-bullet-item">• {line.trim()}</div>
                            ))}
                    </div>
                </div>
            );
        }

        if (section.type === 'formatted-paragraph') {
            // Preserve exact formatting including spaces, tabs, and line breaks
            const formattedContent = (section.content || '').replace(/\n/g, '<br/>').replace(/ /g, '&nbsp;');
            return (
                <div key={block.id} className="pg-project-block">
                    <div className="pg-body-text pg-formatted-text" dangerouslySetInnerHTML={{ __html: formattedContent }} />
                </div>
            );
        }

        return (
            <div key={block.id} className="pg-project-block">
                <p className="pg-body-text">{section.content}</p>
            </div>
        );
    };

    return (
        <>
            {pages.map((pageBlocks, pageIndex) => (
                <div className="pg-page" key={`project-page-${pageIndex}`}>
                    <div className="pg-page-border">
                        {renderHeader(pageIndex)}
                        <div className="pg-prev-sections-box pg-paginated-sections-box">
                            {pageBlocks.length ? pageBlocks.map(renderBlock) : (
                                <p className="pg-body-text">Add project details to preview here.</p>
                            )}
                        </div>
                        {renderFooter(pageIndex)}
                    </div>
                </div>
            ))}
        </>
    );
}

export default PaginatedProjectPreview;
